import { auth } from './config';

/* The orgId custom claim is minted server-side by the syncOrgClaims trigger, which fires after
   the users/{uid} doc is written — so right after org creation or invite acceptance the client
   is holding a token that predates the claim, and Storage rules will deny it.

   Firebase only picks up new claims on token refresh (otherwise up to an hour), so reconcile
   explicitly: read the cached token first (free, and already correct in the common case), then
   force-refresh with backoff until the claim shows up. Bounded at ~7.5s across four attempts
   because a cold-started trigger can take a few seconds; callers run this in the background
   rather than blocking the UI on it.

   Returns whether the claim matched. Upload failures are still retried independently in
   storage.ts, so a false here degrades to one retried upload rather than a broken feature.

   The role claim is checked too: after an admin changes someone's role the claim is stale in
   the same way (a promoted contractor would stay fenced to assigned projects until the hourly
   refresh). `expectedRole` undefined skips that check; null means the profile has no role. */
export async function ensureOrgClaim(
  expectedOrgId: string, expectedRole?: string | null, attempts = 4,
): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  for (let i = 0; i < attempts; i++) {
    try {
      const result = await user.getIdTokenResult(i > 0);
      const claims = result.claims as { orgId?: string; role?: string | null };
      if (claims.orgId === expectedOrgId &&
          (expectedRole === undefined || (claims.role ?? null) === expectedRole)) return true;
    } catch (err) {
      console.warn('[claims] token refresh failed', err);
      return false;
    }
    await new Promise(resolve => setTimeout(resolve, 500 * 2 ** i));
  }
  console.warn(`[claims] org claims did not match org ${expectedOrgId} / role ${expectedRole} after ${attempts} tries`);
  return false;
}
